import { useEffect, type RefObject } from 'react';
import { gsap } from 'FrontRoyalGsap';
import { useAngularContext } from 'AngularContext';
import { type AnyObject } from '@Types';

/*
    Bounces the certificate in once after the student finishes the course, then clears completedStreamIndex.
    onBounce runs with no certificate element too, so a course without a certificate image still gets the
    transcripts reminder.
*/
export function useCertificateBounce(
    certificateRef: RefObject<HTMLElement | null>,
    streamId: string,
    onBounce: () => void,
) {
    const $injector = useAngularContext();

    useEffect(() => {
        const StreamProgressionStatus = $injector.get<AnyObject>('StreamProgressionStatus');
        if (StreamProgressionStatus.completedStreamIndex !== streamId) return;

        StreamProgressionStatus.completedStreamIndex = undefined;

        const certificate = certificateRef.current;
        if (certificate) {
            gsap.fromTo(certificate, { y: -300 }, { duration: 1.2, y: 0, ease: 'bounce.out' });
            gsap.to(certificate, { duration: 0.25, scale: 1.1, delay: 1.5, ease: 'power1.in' });
            gsap.to(certificate, { duration: 0.25, scale: 1, delay: 2, ease: 'power1.out' });
        }

        onBounce();
    }, [certificateRef, streamId, $injector, onBounce]);
}
